import { Box, Button, Dialog, MenuItem, Radio, Select, TextField, Typography } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import ModalCard from "../../modal/ModalCard";

interface RecurrenceSelectProps {
  defaultRecurrenceOption: string;
  setDefaultRecurrenceOption: Dispatch<SetStateAction<string>>;
  setSelectedDates: Dispatch<SetStateAction<Date[]>>;
  manuallySelectedDates: Date[];
  postRecurrenceDates: Date[];
  scheduledDates: Date[];
  colors: Map<string, string>;
}

export const NO_RECURRENCE = "no-recurrence";
const WEEKLY = "weekly";
const MONTHLY = "monthly";
const YEARLY = "yearly";
const CUSTOM = "custom";
const MAX_INTERVAL = 11;
const MAX_ITERATIONS = 1000;

const RecurrenceSelect = ({
  defaultRecurrenceOption,
  setDefaultRecurrenceOption,
  setSelectedDates,
  manuallySelectedDates,
  postRecurrenceDates,
  scheduledDates,
  colors,
}: RecurrenceSelectProps) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [interval, setInterval] = useState(1);
  const [endOption, setEndOption] = useState<"after" | "endDate">("endDate");
  const [occurrences, setOccurrences] = useState(10);
  const [endDate, setEndDate] = useState<Date>(moment().add(1, "month").toDate());
  const [pattern, setPattern] = useState("weekly");
  const [weeklyDays, setWeeklyDays] = useState<string[]>([]);

  const daysOfWeek = [
    { value: "mo", label: t("header.days.mon") },
    { value: "tu", label: t("header.days.tue") },
    { value: "we", label: t("header.days.wed") },
    { value: "th", label: t("header.days.thu") },
    { value: "fr", label: t("header.days.fri") },
    { value: "sa", label: t("header.days.sat") },
    { value: "su", label: t("header.days.sun") },
  ];

  useEffect(() => {
    if (manuallySelectedDates.length > 0) {
      const minDate = moment.min(manuallySelectedDates.map(date => moment(date))).toDate();
      setStartDate(minDate);
      setEndDate(moment(minDate).add(1, "month").toDate());
    }
  }, [manuallySelectedDates]);

  const handleRecurrenceSave = () => {
    const dates = generateRecurrenceDates();
    const uniqueDates = Array.from(new Set(dates.map(date => date.getTime())))
      .map(time => new Date(time));

    const filteredDates = uniqueDates.filter(date =>
      !scheduledDates.some(scheduledDate =>
        moment(scheduledDate).isSame(date, "day")
      )
    );

    setSelectedDates([...scheduledDates, ...filteredDates]);
    setIsModalOpen(false);
  };

  const generateRecurrenceDates = () => {
    let currentDate = moment(startDate);
    const dates: Date[] = [];
    let count = 0;
    const safeInterval = Math.max(1, Math.min(interval, MAX_INTERVAL));

    const targetDays = weeklyDays.map(d =>
      daysOfWeek.findIndex(day => day.value === d) + 1
    ).filter(d => d >= 1 && d <= 7);

    if (pattern === "weekly" && targetDays.length > 0) {
      let safety = 0;
      while (!targetDays.includes(currentDate.isoWeekday()) && safety < 7) {
        currentDate = currentDate.add(1, 'day');
        safety++;
      }
    }

    while (count < MAX_ITERATIONS) {
      if (endOption === "after" && count >= occurrences) break;
      if (endOption === "endDate" && currentDate.isAfter(endDate)) break;

      if (currentDate.isSameOrAfter(moment(), 'day')) {
        dates.push(currentDate.toDate());
      }

      switch (pattern) {
        case "daily":
          currentDate = currentDate.add(safeInterval, "days");
          break;

        case "weekly":
          if (targetDays.length > 0) {
            const currentWeekDay = currentDate.isoWeekday();
            let daysToAdd = 0;
            let found = false;

            for (let d = currentWeekDay + 1; d <= 7; d++) {
              if (targetDays.includes(d)) {
                daysToAdd = d - currentWeekDay;
                found = true;
                break;
              }
            }

            if (!found) {
              const weeksToAdd = safeInterval - 1;
              daysToAdd = (7 - currentWeekDay) +
                Math.min(...targetDays) +
                (7 * weeksToAdd);
            }

            daysToAdd = Math.max(daysToAdd, 1);
            currentDate = currentDate.add(daysToAdd, "days");
          } else {
            currentDate = currentDate.add(safeInterval, "weeks");
          }
          break;

        case "monthly":
          currentDate = currentDate.add(safeInterval, "months");
          break;

        case "yearly":
          currentDate = currentDate.add(safeInterval, "years");
          break;
      }

      count++;
      if (count >= MAX_ITERATIONS) break;
    }

    return dates;
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={() => setIsModalOpen(true)}
        disabled={manuallySelectedDates.length === 0}
        sx={{ m: 2 }}
      >
        {t("header.RecurringDates.setRecurrence")}
      </Button>

      <Dialog open={isModalOpen} onClose={() => setIsModalOpen(false)} maxWidth="md">
        <ModalCard
          title={t("header.RecurringDates.recurrencePattern")}
          closeCallback={() => setIsModalOpen(false)}
        >
          <ModalCard.Main isDivider={false}>
            <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography>{t("header.RecurringDates.start")}</Typography>
                <DatePicker
                  value={startDate}
                  onChange={(newValue) => newValue && setStartDate(newValue)}
                  disabled
                />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography>{t("header.RecurringDates.repeatEvery")}</Typography>
                <TextField
                  type="number"
                  value={interval}
                  onChange={(e) => setInterval(Math.max(1, Math.min(parseInt(e.target.value) || 1, MAX_INTERVAL)))}
                  sx={{ width: 80 }}
                />
                <Select
                  value={pattern}
                  onChange={(e) => setPattern(e.target.value)}
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="daily">{t("header.RecurringDates.days")}</MenuItem>
                  <MenuItem value="weekly">{t("header.RecurringDates.weeks")}</MenuItem>
                  <MenuItem value="monthly">{t("header.RecurringDates.months")}</MenuItem>
                  <MenuItem value="yearly">{t("header.RecurringDates.years")}</MenuItem>
                </Select>
              </Box>

              {pattern === "weekly" && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {daysOfWeek.map(day => (
                      <Button
                        key={day.value}
                        variant={weeklyDays.includes(day.value) ? "contained" : "outlined"}
                        onClick={() => setWeeklyDays(prev =>
                          prev.includes(day.value)
                            ? prev.filter(d => d !== day.value)
                            : [...prev, day.value]
                        )}
                        sx={{
                          minWidth: 40,
                          padding: '4px 8px',
                          fontSize: '0.875rem'
                        }}
                      >
                        {day.label}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <Typography>{t("header.RecurringDates.end")}</Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Radio
                    checked={endOption === "after"}
                    onChange={() => setEndOption("after")}
                    color="primary"
                  />
                  <Typography>{t("header.RecurringDates.after")}</Typography>

                  <TextField
                    type="number"
                    value={occurrences}
                    onChange={(e) => setOccurrences(Math.max(1, parseInt(e.target.value) || 1))}
                    sx={{ width: 100 }}
                    disabled={endOption !== "after"}
                  />
                  <Typography>{t("header.RecurringDates.occurrences")}</Typography>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <Radio
                    checked={endOption === "endDate"}
                    onChange={() => setEndOption("endDate")}
                    color="primary"
                  />
                  <Typography>{t("header.RecurringDates.endBy")}</Typography>

                  <DatePicker
                    value={endDate}
                    onChange={(newValue) => newValue && setEndDate(newValue)}
                    disabled={endOption !== "endDate"}
                    sx={{ flexGrow: 1 }}
                  />
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                <Button
                  onClick={() => setIsModalOpen(false)}
                  variant="outlined"
                >
                  {t("header.cancel")}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleRecurrenceSave}
                  color="primary"
                >
                  {t("button.addPeriodic")}
                </Button>
              </Box>
            </Box>
          </ModalCard.Main>
        </ModalCard>
      </Dialog>
    </>
  );
};

export default RecurrenceSelect;